import styled from '@emotion/styled';
import React from 'react';

import { distributeDeprecatedWarning } from '../../console-messages';
import type { LensDivProps, ResponsiveType } from '../../types';
import {
  arrayToBreakpoints,
  getAlignmentStyles,
  getResponsiveBoolean,
  getSizeValue,
} from '../../utilities';

const alignments = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
};

let alreadyWarned = false;

const propToArray = prop => {
  if (!Array.isArray(prop)) {
    return [prop];
  }

  return prop;
};

const formatItems = (items, targetRulesetLength) => {
  if (items.length === targetRulesetLength) {
    return items;
  }

  const lastItem = items[items.length - 1];

  return [...Array(targetRulesetLength)].map((_, i) => items[i] || lastItem);
};

const getDirectionAndGap = (direction, gap) => {
  const targetRulesetLength = Math.max(
    propToArray(direction).length,
    propToArray(gap).length
  );

  const gapsArray = formatItems(propToArray(gap), targetRulesetLength);
  const directionsArray = formatItems(
    propToArray(direction),
    targetRulesetLength
  );

  const childrenSelector = '& > * + *';
  const gapsRulesets = gapsArray.map((gapItem, index) => {
    const marginTop = `${getSizeValue(gapItem)} 0 0 0`;
    const marginRight = `0 0 0 ${getSizeValue(gapItem)}`;

    const marginValue =
      directionsArray[index] === 'column' ? marginTop : marginRight;

    return `${childrenSelector}{ margin: ${marginValue}; }`;
  });

  const directionsRulesets = directionsArray.map(
    directionItem => `flex-direction: ${directionItem}`
  );

  return (
    arrayToBreakpoints(gapsRulesets) + arrayToBreakpoints(directionsRulesets)
  );
};

const DistributeWrapper = styled.div<DistributeWrapperProps>`
  display: flex;
  flex-wrap: wrap;
  ${props => getDirectionAndGap(props.flexDirection, props.gap)};
  ${props => getAlignmentStyles(props.flexAlign, alignments)};
  ${props =>
    props.isSpread &&
    getResponsiveBoolean(
      'justify-content',
      ['space-between', 'initial'],
      props.isSpread
    )};

  & > * {
    flex-shrink: 0;
  }
`;

const Distribute = ({
  children,
  gap,
  direction = 'row',
  alignment = 'start',
  isSpread,
  htmlTag = 'div',
  ...props
}: DistributeProps &
  React.ComponentProps<typeof DistributeWrapper> &
  Omit<
    LensDivProps,
    keyof DistributeProps & React.ComponentProps<typeof DistributeWrapper>
  >) => {
  if (!alreadyWarned) {
    // eslint-disable-next-line no-console
    console.warn(distributeDeprecatedWarning);
    alreadyWarned = true;
  }

  return (
    <DistributeWrapper
      gap={gap}
      flexDirection={direction}
      flexAlign={alignment}
      isSpread={isSpread}
      as={htmlTag}
      {...props}
    >
      {children}
    </DistributeWrapper>
  );
};

export const availableDirections = ['row', 'column'];
export const availableAlignments = Object.keys(alignments);
export const availableHtmlTags = [
  'div',
  'header',
  'article',
  'section',
  'nav',
  'aside',
  'footer',
  'main',
];

type Alignment = 'start' | 'center' | 'end' | 'stretch';
type DistributeProps = {
  children: React.ReactNode;
  direction?: ResponsiveType<'row' | 'column'>;
  alignment?: Alignment | Alignment[];
  isSpread?: ResponsiveType<true | false>;
  htmlTag?:
    | 'div'
    | 'header'
    | 'article'
    | 'section'
    | 'nav'
    | 'aside'
    | 'footer'
    | 'main';
  gap?: string | number;
};

type DistributeWrapperProps = {
  flexDirection?: ResponsiveType<'row' | 'column'>;
  flexAlign?: Alignment | Alignment[];
  isSpread?: boolean | boolean[];
  gap?: string | number;
  as?: string;
};

export default Distribute;
